// src/pages/NotificationsPage/notificationUtils.js
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';

// Utility to group notifications by conversation chain
// Groups notifications that are part of the same meeting invite flow
// (initial invite -> suggest -> suggest -> accept/reject)
// Once a conversation is accepted/rejected, new invites start a NEW group
export function groupNotifications(notifications) {
  // First, sort all notifications by creation time (oldest first)
  const sortedNotes = [...notifications].sort((a, b) => {
    const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return timeA - timeB;
  });
  
  const grouped = {};
  const conversationSessions = {}; // Track which session each buyer/seller/listing is on
  
  for (const note of sortedNotes) {
    const buyerId = note.buyerId || 'unknown';
    const sellerId = note.sellerId || 'unknown';
    const listingId = note.listingId || 'unknown';
    const baseKey = `${buyerId}__${sellerId}__${listingId}`;
    
    // Initialize session counter for this base key if not exists
    if (!conversationSessions[baseKey]) {
      conversationSessions[baseKey] = 0;
    }
    
    // Check if we need to start a NEW session
    // New session starts if:
    // 1. This is the first notification for this base key, OR
    // 2. The previous session ended (accepted/rejected) and this is a new invite
    if (grouped[`${baseKey}__${conversationSessions[baseKey]}`]) {
      const currentGroup = grouped[`${baseKey}__${conversationSessions[baseKey]}`];
      const lastInGroup = currentGroup[currentGroup.length - 1];
      
      // If last notification in current group is accepted/rejected, start new session
      if (lastInGroup.status === 'accepted' || lastInGroup.status === 'rejected') {
        conversationSessions[baseKey]++;
      }
    }
    
    // Create group key with session number
    const groupKey = `${baseKey}__${conversationSessions[baseKey]}`;
    
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(note);
  }
  
  return grouped;
}

// Helper: Determines if the current user can respond to a notification
export function canRespondToNotification(note, user, justSuggestedId) {
  // RULE: Once accepted or rejected, NO ONE can respond - flow ends
  if (note.status === 'accepted' || note.status === 'rejected') return false;
  
  // RULE: You cannot respond if you were the last person to act
  // This prevents the person who just suggested/sent from acting again
  if (note.lastActionBy && user.uid === note.lastActionBy) return false;
  
  // RULE: Prevent double-clicking on suggest (temporary UI lock)
  if (note.id === justSuggestedId) return false;
  
  // RULE: Only the recipient can respond
  return note.recipientId === user.uid;
}

// Helper: Handles state update and alert for notification actions (accept/reject/suggest)
export function handleNotificationAction(
  { setNotifications, setRespondingId, setJustSuggestedId, setResponse, note, response }, 
  action
) {
  // Handle local state update for each action
  if (action === 'suggested') {
    // Immediately set justSuggestedId to hide action buttons for this note
    setJustSuggestedId(note.id);
    setNotifications(prev => prev.map(n => 
      n.id === note.id 
        ? { 
            ...n, 
            status: 'pending', 
            responseMessage: response.message, 
            responseDate: response.date, 
            responseTime: response.time 
          } 
        : n
    ));
    setRespondingId(null);
    setResponse({ status: '', message: '', date: '', time: '' });
  } else if (action === 'accepted') {
    setNotifications(prev => prev.map(n => 
      n.id === note.id ? { ...n, status: 'accepted' } : n
    ));
    setRespondingId(null);
    setResponse({ status: '', message: '', date: '', time: '' });
    setTimeout(() => {
      alert('Invite accepted.');
    }, 0);
  } else if (action === 'rejected') {
    setNotifications(prev => prev.map(n => 
      n.id === note.id ? { ...n, status: 'rejected' } : n
    ));
    setRespondingId(null);
    setResponse({ status: '', message: '', date: '', time: '' });
    setTimeout(() => {
      alert('Invite rejected.');
    }, 0);
  }
}

// Utility to get the other party's user ID for notification
export function getOtherPartyUserId(note, currentUserId) {
  // If you are the sender, the recipient is the other party, and vice versa
  return note.senderId === currentUserId ? note.recipientId : note.senderId;
}

// Helper to update invite status in Firestore
export async function updateInviteStatus(inviteId, status, message, date, time, isSuggestion = false, location = null) {
  const inviteRef = doc(db, 'notifications', inviteId);
  const updateData = {
    status,
    lastActionBy: auth.currentUser?.uid || null,
  };
  
  // Only update these fields if they're provided
  if (message !== undefined && message !== null) {
    updateData.responseMessage = message || null;
  }
  if (date !== undefined && date !== null) {
    updateData.responseDate = date || null;
  }
  if (time !== undefined && time !== null) {
    updateData.responseTime = time || null;
  }
  
  // If it's a suggestion, update the main date/time/location fields as well
  if (isSuggestion && date && time) {
    updateData.date = date;
    updateData.time = time;
    // Update location if provided
    if (location) {
      updateData.location = location;
    }
  }
  
  await updateDoc(inviteRef, updateData);
}